<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useDealStagesStore } from 'dashboard/stores/dealStages';
import { useDealsStore } from 'dashboard/stores/deals';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import CrmColumn from './CrmColumn.vue';
import CrmDealCreateDialog from './CrmDealCreateDialog.vue';
import CrmDealEditDialog from './CrmDealEditDialog.vue';
import CrmScheduleDialog from './CrmScheduleDialog.vue';
import CrmTasksDialog from './CrmTasksDialog.vue';
import StageConfigDialog from './StageConfigDialog.vue';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const store = useStore();
const dealStagesStore = useDealStagesStore();
const dealsStore = useDealsStore();

const agents = useMapGetter('agents/getAgents');

const hasError = ref(false);
const createDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);
const stageConfigDialogRef = ref(null);
const scheduleDialogRef = ref(null);
const tasksDialogRef = ref(null);
const selectedDeal = ref(null);
const isDeleting = ref(false);

const stages = computed(() => dealStagesStore.orderedStages);
const isLoading = computed(() => dealStagesStore.getUIFlags.fetchingList);
const dealsForStage = stageId => dealsStore.dealsForStage(stageId);

// Client-side only — it filters the deals already loaded for page 1 of each
// stage. A deal sitting on page 2+ (>25 in a stage) won't be considered; see
// the task report for why this is an accepted MVP limitation.
const followUpFilter = ref('all');
const followUpFilterOptions = computed(() => [
  { value: 'all', label: t('CRM_FUNIL.FOLLOW_UP.FILTER.ALL') },
  { value: 'today', label: t('CRM_FUNIL.FOLLOW_UP.FILTER.TODAY') },
  { value: 'overdue', label: t('CRM_FUNIL.FOLLOW_UP.FILTER.OVERDUE') },
]);

// Same TZ-safe ISO string comparison as CrmCard's `followUpStatus`.
const dealFollowUpStatus = deal => {
  if (!deal.nextFollowUpOn) return null;
  const today = new Date().toLocaleDateString('en-CA');
  if (deal.nextFollowUpOn < today) return 'overdue';
  if (deal.nextFollowUpOn === today) return 'today';
  return 'future';
};

const visibleDeals = stageId => {
  const deals = dealsForStage(stageId);
  if (followUpFilter.value === 'all') return deals;
  return deals.filter(
    deal => dealFollowUpStatus(deal) === followUpFilter.value
  );
};

// Server-side filters (Fase 2): search, owner, status and value range,
// applied on `deals#index` and re-fetched for every stage on change. They
// compose with the client-side follow-up filter above, which only ever
// narrows down whatever `visibleDeals` already received.
const filters = reactive({
  q: '',
  assigneeId: '',
  valueMin: '',
  valueMax: '',
  status: '',
});

const hasActiveFilters = computed(() =>
  Object.values(filters).some(value => value !== '')
);

const assigneeOptions = computed(() => [
  { value: '', label: t('CRM_FUNIL.FILTERS.ALL') },
  ...(agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const statusOptions = computed(() => [
  { value: '', label: t('CRM_FUNIL.FILTERS.ALL') },
  { value: 'open', label: t('CRM_FUNIL.FILTERS.STATUS_OPEN') },
  { value: 'won', label: t('CRM_FUNIL.FILTERS.STATUS_WON') },
  { value: 'lost', label: t('CRM_FUNIL.FILTERS.STATUS_LOST') },
]);

const activeFilters = () => {
  const active = {};
  if (filters.q) active.q = filters.q;
  if (filters.assigneeId) active.assigneeId = filters.assigneeId;
  if (filters.valueMin !== '') active.valueMin = filters.valueMin;
  if (filters.valueMax !== '') active.valueMax = filters.valueMax;
  if (filters.status) active.status = filters.status;
  return active;
};

const reloadDeals = () =>
  Promise.all(
    dealStagesStore.orderedStages.map(stage =>
      dealsStore.fetchByStage(stage.id, 1, activeFilters())
    )
  );

const onSearchInput = useDebounceFn(value => {
  filters.q = value;
  reloadDeals();
}, 300);

const onFilterChange = () => reloadDeals();

const clearFilters = () => {
  filters.q = '';
  filters.assigneeId = '';
  filters.valueMin = '';
  filters.valueMax = '';
  filters.status = '';
  reloadDeals();
};

const loadBoard = async () => {
  hasError.value = false;
  try {
    await dealStagesStore.fetch();
    await Promise.all([
      ...dealStagesStore.orderedStages.map(stage =>
        dealsStore.fetchByStage(stage.id, 1, activeFilters())
      ),
      dealsStore.fetchSummary(),
    ]);
  } catch {
    hasError.value = true;
  }
};

// Each currency in a totals map is formatted and joined on its own — R$ and
// $ are never summed together.
const formatCurrencyMap = (map = {}) => {
  const parts = Object.entries(map)
    .filter(([, cents]) => cents != null)
    .map(([currency, cents]) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format((cents || 0) / 100)
    );
  return parts.join(' · ');
};

const totalsForStage = stageId =>
  dealsStore.summary.byStage[stageId] ||
  dealsStore.summary.byStage[String(stageId)] ||
  {};

// The column header hides an empty total entirely, but the summary panel
// always shows a figure — an empty map falls back to a zeroed BRL amount.
const formatSummaryMap = map =>
  formatCurrencyMap(map) || formatCurrencyMap({ BRL: 0 });

const openSummary = computed(() =>
  formatSummaryMap(dealsStore.summary.totals.open)
);
const wonMonthSummary = computed(() =>
  formatSummaryMap(dealsStore.summary.totals.wonMonth)
);
const lostMonthSummary = computed(() =>
  formatSummaryMap(dealsStore.summary.totals.lostMonth)
);

const openCreateDialog = () => createDialogRef.value?.open();
const openStageConfigDialog = () => stageConfigDialogRef.value?.open();

const onEditDeal = deal => {
  selectedDeal.value = deal;
  editDialogRef.value?.open?.(deal);
};

const onDeleteDeal = deal => {
  selectedDeal.value = deal;
  deleteDialogRef.value?.open?.();
};

const onScheduleDeal = deal => {
  selectedDeal.value = deal;
  scheduleDialogRef.value?.open?.(deal);
};

const onTasks = deal => {
  selectedDeal.value = deal;
  tasksDialogRef.value?.open?.(deal);
};

const confirmDeleteDeal = async () => {
  if (!selectedDeal.value) return;

  isDeleting.value = true;
  try {
    await dealsStore.deleteDeal(selectedDeal.value.id);
    useAlert(t('CRM_FUNIL.DELETE_DEAL.SUCCESS'));
    deleteDialogRef.value?.close?.();
  } catch {
    useAlert(t('CRM_FUNIL.DELETE_DEAL.ERROR'));
  } finally {
    isDeleting.value = false;
    selectedDeal.value = null;
  }
};

onMounted(() => {
  if (!agents.value?.length) store.dispatch('agents/get');
  loadBoard();
});

defineExpose({
  loadBoard,
  onEditDeal,
  onDeleteDeal,
  onScheduleDeal,
  onTasks,
  confirmDeleteDeal,
  selectedDeal,
  openSummary,
  wonMonthSummary,
  lostMonthSummary,
  followUpFilter,
  visibleDeals,
  filters,
  hasActiveFilters,
  onSearchInput,
  onFilterChange,
  clearFilters,
});
</script>

<template>
  <div class="flex flex-col w-full h-full p-4 gap-4">
    <div class="flex items-center justify-between">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('CRM_FUNIL.HEADER.TITLE') }}
      </h1>
      <div class="flex items-center gap-2">
        <Select
          v-model="followUpFilter"
          :options="followUpFilterOptions"
          :aria-label="t('CRM_FUNIL.FOLLOW_UP.FILTER.ALL')"
        />
        <Button
          v-if="isAdmin"
          :label="t('CRM_FUNIL.STAGE_CONFIG.BUTTON')"
          icon="i-lucide-settings"
          variant="faded"
          color="slate"
          size="sm"
          @click="openStageConfigDialog"
        />
        <Button
          :label="t('CRM_FUNIL.NEW_DEAL.BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <div class="flex flex-wrap gap-2 items-center">
      <Input
        :model-value="filters.q"
        type="search"
        class="w-48"
        :placeholder="t('CRM_FUNIL.FILTERS.SEARCH_PLACEHOLDER')"
        @update:model-value="onSearchInput"
      />
      <Select
        :model-value="filters.assigneeId"
        :options="assigneeOptions"
        :aria-label="t('CRM_FUNIL.FILTERS.ASSIGNEE')"
        @update:model-value="
          value => {
            filters.assigneeId = value;
            onFilterChange();
          }
        "
      />
      <Select
        :model-value="filters.status"
        :options="statusOptions"
        :aria-label="t('CRM_FUNIL.FILTERS.STATUS')"
        @update:model-value="
          value => {
            filters.status = value;
            onFilterChange();
          }
        "
      />
      <Input
        :model-value="filters.valueMin"
        type="number"
        class="w-28"
        :placeholder="t('CRM_FUNIL.FILTERS.VALUE_MIN')"
        @update:model-value="
          value => {
            filters.valueMin = value;
            onFilterChange();
          }
        "
      />
      <Input
        :model-value="filters.valueMax"
        type="number"
        class="w-28"
        :placeholder="t('CRM_FUNIL.FILTERS.VALUE_MAX')"
        @update:model-value="
          value => {
            filters.valueMax = value;
            onFilterChange();
          }
        "
      />
      <Button
        v-if="hasActiveFilters"
        :label="t('CRM_FUNIL.FILTERS.CLEAR')"
        variant="faded"
        color="slate"
        size="sm"
        @click="clearFilters"
      />
    </div>

    <div class="flex flex-wrap items-center gap-4 text-xs">
      <span class="text-n-slate-11">
        {{ `${t('CRM_FUNIL.SUMMARY.OPEN')}:` }}
        <span class="font-medium text-n-slate-12">{{ openSummary }}</span>
      </span>
      <span class="text-n-slate-11">
        {{ `${t('CRM_FUNIL.SUMMARY.WON_MONTH')}:` }}
        <span class="font-medium text-n-teal-11">{{ wonMonthSummary }}</span>
      </span>
      <span class="text-n-slate-11">
        {{ `${t('CRM_FUNIL.SUMMARY.LOST_MONTH')}:` }}
        <span class="font-medium text-n-ruby-9">{{ lostMonthSummary }}</span>
      </span>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-slate-11">{{ t('CRM_FUNIL.LOADING') }}</span>
    </div>
    <div v-else-if="hasError" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-ruby-9">{{ t('CRM_FUNIL.ERROR') }}</span>
    </div>
    <div v-else class="flex flex-1 gap-4 overflow-x-auto">
      <CrmColumn
        v-for="stage in stages"
        :key="stage.id"
        :stage="stage"
        :deals="visibleDeals(stage.id)"
        :totals="totalsForStage(stage.id)"
        @edit="onEditDeal"
        @delete="onDeleteDeal"
        @schedule="onScheduleDeal"
        @tasks="onTasks"
      />
    </div>

    <CrmDealCreateDialog ref="createDialogRef" @success="loadBoard" />
    <CrmDealEditDialog ref="editDialogRef" @success="loadBoard" />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('CRM_FUNIL.DELETE_DEAL.CONFIRM')"
      :is-loading="isDeleting"
      @confirm="confirmDeleteDeal"
    />
    <StageConfigDialog ref="stageConfigDialogRef" @close="loadBoard" />
    <CrmScheduleDialog ref="scheduleDialogRef" />
    <CrmTasksDialog ref="tasksDialogRef" />
  </div>
</template>
